import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Shield } from '@/lib/icons';

import { useToast } from '@/hooks/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import { ButtonSpinner } from '@/components/feedback';
import { forgotPassword } from '@/features/auth/api';

const createForgotPasswordSchema = (t: (key: string) => string) =>
  z.object({
    identifier: z
      .string()
      .min(5, t('auth.validation.identifierMinLength'))
      .refine(
        (val) => {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          const phoneRegex = /^[0-9+\s-]+$/;
          return emailRegex.test(val) || phoneRegex.test(val);
        },
        { message: t('auth.validation.identifierInvalid') }
      ),
  });

type ForgotPasswordFormValues = z.infer<
  ReturnType<typeof createForgotPasswordSchema>
>;

export const Route = createFileRoute('/(auth)/forgot-password')({
  component: ForgotPasswordComponent,
});

function ForgotPasswordComponent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const forgotPasswordSchema = createForgotPasswordSchema(t);

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      identifier: '',
    },
  });

  const onSubmit = async (values: ForgotPasswordFormValues) => {
    const result = await forgotPassword(values.identifier);

    if (!result.succeeded) {
      toast({
        title: t('auth.forgotPassword.toast.errorTitle'),
        description: t('auth.forgotPassword.toast.errorDescription'),
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: t('auth.forgotPassword.toast.successTitle'),
      description: t('auth.forgotPassword.toast.successDescription'),
    });

    navigate({ to: '/login' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -right-[20%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-primary/8 to-primary/3 blur-3xl animate-pulse" />
        <div className="absolute -bottom-[30%] -left-[15%] w-[50%] h-[70%] rounded-full bg-gradient-to-tr from-primary/6 to-transparent blur-3xl" />
      </div>

      <div className="w-full max-w-[420px] space-y-6 relative z-10">
        {/* Back button */}
        <Button
          variant="ghost"
          className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
          onClick={() => navigate({ to: '/login' })}
        >
          <ArrowLeft className="h-4 w-4" />
          {t('auth.forgotPassword.backToLogin')}
        </Button>

        {/* Card */}
        <div className="bg-[hsl(var(--surface-container-lowest)/0.85)] backdrop-blur-xl rounded-2xl border border-[hsl(var(--foreground)/0.06)] shadow-[0_24px_40px_-8px_hsl(213_20%_15%/0.08)] p-8 space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
              <Mail className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold tracking-tight">
              {t('auth.forgotPassword.title')}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('auth.forgotPassword.description')}
            </p>
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="identifier"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('auth.forgotPassword.identifierLabel')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t(
                          'auth.forgotPassword.identifierPlaceholder'
                        )}
                        className="h-11 rounded-xl bg-muted/50 border-border/50 focus-visible:ring-primary/30"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full h-11 rounded-xl font-semibold text-sm bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    {t('common.loading.default')}
                  </>
                ) : (
                  t('auth.forgotPassword.submitButton')
                )}
              </Button>
            </form>
          </Form>
        </div>

        {/* Security badge */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70">
          <Shield className="h-3.5 w-3.5" />
          <span>Secured with end-to-end encryption</span>
        </div>
      </div>
    </div>
  );
}
